import React, { useState, useEffect } from 'react';
import Joyride, { CallBackProps, STATUS, Step } from 'react-joyride';
import { Button, message } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';

interface TutorialGuideProps {
  viewType: 'card' | 'table';
  stage: string;
  externalStartTourRef?: React.MutableRefObject<(() => void) | null>;
}

const TutorialGuide: React.FC<TutorialGuideProps> = ({ viewType, stage, externalStartTourRef }) => {
  const [runTour, setRunTour] = useState(false);
  const [firstTime, setFirstTime] = useState(true);
  
  // Simple startTour function that always works
  const startTour = () => {
    console.log('Starting tour immediately...'); // Debug log
    setRunTour(false); // Reset first
    setTimeout(() => {
      setRunTour(true);
    }, 100);
  };

  // Expose the startTour function to parent components
  useEffect(() => {
    if (externalStartTourRef) {
      externalStartTourRef.current = startTour;
    }
  }, [externalStartTourRef]);
  
  // Check if this is the user's first visit
  useEffect(() => {
    const hasSeenTutorial = localStorage.getItem('talentAwardsTutorialSeen');
    if (!hasSeenTutorial && firstTime) {
      // Show a message suggesting the tutorial
      message.info(
        'Welcome! Click the help button in the bottom right corner to learn about this page.', 
        6
      );
      setFirstTime(false);
    }
  }, [firstTime]);

  // Define tutorial steps based on view type and stage
  const getSteps = (): Step[] => {
    const baseSteps: Step[] = [
      {
        target: 'body',
        content: 'Welcome to the Awards Portal! Let me show you around this page.',
        placement: 'center',
      },
      {
        target: '.usergrid-header-bar',
        content: 'This is the main header area where you can control your view and access filters.',
        placement: 'bottom',
      },
      {
        target: '.usergrid-header-title',
        content: 'This shows you the current award category you are viewing. Click "View criteria" to learn more about the award criteria.',
        placement: 'bottom',
      },
      {
        target: '.usergrid-filter',
        content: 'Use these filters to narrow down nominees by product name or business unit.',
        placement: 'bottom',
      },
      {
        target: '.usergrid-header-actions',
        content: 'Switch between card view and table view using these buttons. Card view shows more details, while table view is better for comparing nominees.',
        placement: 'bottom',
      }
    ];

    // Add view-specific steps
    if (viewType === 'card') {
      baseSteps.push({
        target: '.ant-col:first-child',
        content: 'Each card represents a nominee. You can see basic information like name, product, and business unit.',
        placement: 'right',
      });
      baseSteps.push({
        target: '.review-button',
        content: 'Click this "Review" button to see the full details of the nomination. This will open a modal with the nominee\'s achievements, contributions, and all supporting information provided by the nominator.',
        placement: 'top',
      });
    } else {
      baseSteps.push({
        target: '.ant-table',
        content: 'This table shows all nominees. You can sort by different columns and review each nomination.',
        placement: 'top',
      });
      baseSteps.push({
        target: '.review-button',
        content: 'Click "Review" to see the full nomination details.',
        placement: 'left',
      });
    }

    // Add stage-specific steps
    if (stage === '6' || stage === '8') {
      baseSteps.push({
        target: '.usergrid-content',
        content: 'As an ELT member, you need to evaluate each nomination. You have three options:',
        placement: 'top',
      });
      
      baseSteps.push({
        target: viewType === 'table' ? '.ant-table-tbody' : '.ant-col:first-child',
        content: 'Support: You believe this nomination deserves the award\nReject: You believe this nomination should not receive the award\nNo Visibility: You don\'t have enough information to make a decision',
        placement: 'bottom',
      });
    }
    
    if (stage === '7' || stage === '9') {
      baseSteps.push({
        target: '.usergrid-content',
        content: 'You can see rankings and vote counts to help make final decisions.',
        placement: 'top',
      });

      if (viewType === 'table') {
        baseSteps.push({
          target: 'th.ant-table-cell:nth-child(6)',
          content: 'The Votes section shows how many ELT members supported, rejected, or had no visibility on each nomination.',
          placement: 'bottom',
        });
        
        baseSteps.push({
          target: 'th.ant-table-cell:nth-child(7)',
          content: 'The Ranking shows the calculated score based on ELT votes. Higher scores mean more support.',
          placement: 'bottom',
        });
      }
      
      baseSteps.push({
        target: viewType === 'table' ? '.ant-table-tbody' : '.ant-col:first-child',
        content: 'You can approve or reject nominations',
        placement: 'right',
      });
    }

    return baseSteps;
  };

  const handleJoyrideCallback = (data: CallBackProps) => {
    const { status, type } = data;
    console.log('Joyride callback:', { status, type }); // Debug log
    
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      setRunTour(false);
      // Mark that the user has seen the tutorial
      localStorage.setItem('talentAwardsTutorialSeen', 'true');
    }
    
    // Handle errors gracefully
    if (status === STATUS.ERROR) {
      console.error('Joyride error occurred');
      setRunTour(false);
    }
  };

  return (
    <>
      <style>
        {`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 0.95; transform: translateY(10px); }
        }
        
        .react-joyride__tooltip {
          border-radius: 8px !important;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1) !important;
        }
        
        .react-joyride__spotlight {
          border-radius: 4px !important;
        }
        `}
      </style>
      <Joyride
        callback={handleJoyrideCallback}
        continuous
        hideCloseButton
        run={runTour}
        scrollToFirstStep
        showProgress
        showSkipButton
        disableOverlayClose
        disableScrolling={false}
        spotlightPadding={5}
        steps={getSteps()}
        styles={{
          options: {
            zIndex: 10000,
            primaryColor: '#1677ff',
            overlayColor: 'rgba(0, 0, 0, 0.5)',
          },
          tooltip: {
            borderRadius: '8px',
            padding: '16px',
          },
          tooltipContainer: {
            textAlign: 'left',
            fontSize: '14px',
            lineHeight: '1.5',
          },
          buttonBack: {
            marginRight: 10,
            backgroundColor: '#f0f2f5',
            color: '#333',
          },
          buttonNext: {
            backgroundColor: '#1677ff',
          },
          buttonSkip: {
            color: '#999',
          },
          spotlight: {
            backgroundColor: 'transparent',
            borderRadius: '4px',
            boxShadow: '0 0 0 4px rgba(22, 119, 255, 0.3)',
          },
        }}
        locale={{
          back: 'Back',
          close: 'Close',
          last: 'Finish',
          next: 'Next',
          skip: 'Skip tour',
        }}
        floaterProps={{
          hideArrow: false,
          disableAnimation: false,
        }}
      />
    </>
  );
};

export default TutorialGuide;
